import React from 'react';
import { Check, X, ArrowRight, Diamond } from 'lucide-react';
import { comparisonData } from '../../data/mock';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const Comparison = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  
  return (
    <section id="comparison" ref={sectionRef} className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0D0B09]" />
      
      {/* Decorative Orbs */}
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#472F27]/20 rounded-full blur-[200px]" />
      <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-[#C9A97B]/5 rounded-full blur-[150px]" />
      
      {/* Diagonal Pattern */}
      <div className="absolute inset-0 diagonal-pattern opacity-30" />

      {/* Decorative corner lines */}
      <div className="absolute top-10 left-10 hidden lg:block">
        <div className="w-20 h-px bg-gradient-to-r from-[#C9A97B]/30 to-transparent" />
        <div className="w-px h-20 bg-gradient-to-b from-[#C9A97B]/30 to-transparent" />
      </div>
      <div className="absolute top-10 right-10 hidden lg:block">
        <div className="w-20 h-px bg-gradient-to-l from-[#C9A97B]/30 to-transparent ml-auto" />
        <div className="w-px h-20 bg-gradient-to-b from-[#C9A97B]/30 to-transparent ml-auto" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-blurIn' : 'opacity-0'}`}>
          <span className={`inline-block px-4 py-1.5 rounded-full bg-[#C9A97B]/10 text-[#C9A97B] text-sm font-medium mb-4 border border-[#C9A97B]/20 ${isVisible ? 'animate-zoomIn' : 'opacity-0'}`} style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
            Karşılaştırma
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#F5F0E8] mb-4">
            Neden <span className="text-gold-gradient">Tam Ayar</span> Kuyumcu Sistemi?
          </h2>
          
          {/* Decorative line under title */}
          <div className={`flex items-center justify-center gap-3 mb-4 ${isVisible ? 'animate-expandWidth' : 'opacity-0'}`} style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-[#C9A97B]/40" />
            <Diamond className="w-3 h-3 text-[#C9A97B]/40" />
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-[#C9A97B]/40" />
          </div>
          
          <p className="text-[#BFB5A5] text-lg max-w-2xl mx-auto">
            Eski sistemlerden farkımızı görün. Biz basit bir stok programı değil, 
            kuyumcunun dijital beyniyiz.
          </p>
        </div>

        {/* Comparison Table with gradient border */}
        <div className={`relative ${isVisible ? 'animate-scaleIn' : 'opacity-0'}`} style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
          {/* Gradient border effect */}
          <div className="absolute -inset-px bg-gradient-to-br from-[#C9A97B]/20 via-transparent to-[#C9A97B]/20 rounded-2xl" />
          
          <div className="relative bg-gradient-to-br from-[#1F1914] to-[#1A1512] rounded-2xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-[#251E18]">
              {comparisonData.headers.map((header, index) => (
                <div
                  key={header}
                  className={`p-4 lg:p-6 text-center relative ${
                    index === 0
                      ? 'text-[#7A7067]'
                      : index === 1
                      ? 'text-[#BFB5A5] border-x border-[#C9A97B]/10'
                      : 'text-[#C9A97B]'
                  }`}
                >
                  {/* Highlight glow for Tam Ayar column */}
                  {index === 2 && (
                    <div className="absolute inset-0 bg-gradient-to-b from-[#C9A97B]/10 to-transparent" />
                  )}
                  <div className="relative">
                    <span className={`font-display font-semibold ${
                      index === 2 ? 'text-lg lg:text-xl text-gold-gradient' : 'text-sm lg:text-base'
                    }`}>
                      {header}
                    </span>
                    {index === 2 && (
                      <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#C9A97B]/20 text-xs text-[#C9A97B] border border-[#C9A97B]/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A97B] animate-pulse" />
                        Önerilen
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Table Rows */}
            <div className="divide-y divide-[#C9A97B]/10">
              {comparisonData.rows.map((row, index) => (
                <div
                  key={index}
                  className="grid grid-cols-3 hover:bg-[#C9A97B]/5 transition-colors duration-300 group"
                >
                  {/* Feature Name */}
                  <div className="p-4 lg:p-6 flex items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C9A97B]/40 group-hover:bg-[#C9A97B] transition-colors" />
                      <span className="text-[#F5F0E8] font-medium text-sm lg:text-base">
                        {row.feature}
                      </span>
                    </div>
                  </div>

                  {/* Old Systems */}
                  <div className="p-4 lg:p-6 flex items-center border-x border-[#C9A97B]/10">
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <X className="w-3 h-3 text-red-500/70" />
                      </div>
                      <span className="text-[#7A7067] text-sm">{row.old}</span>
                    </div>
                  </div>

                  {/* Tam Ayar */}
                  <div className="p-4 lg:p-6 flex items-center bg-[#C9A97B]/5 relative">
                    {/* Subtle left border accent */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-8 bg-gradient-to-b from-transparent via-[#C9A97B]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#C9A97B]/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#C9A97B]/30 transition-colors">
                        <Check className="w-3 h-3 text-[#C9A97B]" />
                      </div>
                      <span className="text-[#F5F0E8] text-sm">{row.new}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Message with decorative frame */}
        <div className={`mt-12 relative ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '700ms', animationFillMode: 'forwards' }}>
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-8 bg-gradient-to-b from-[#C9A97B]/30 to-transparent" />
          
          <div className="text-center p-6 relative">
            {/* Corner decorations */}
            <div className="absolute top-0 left-1/4 w-12 h-px bg-gradient-to-r from-transparent to-[#C9A97B]/20" />
            <div className="absolute top-0 right-1/4 w-12 h-px bg-gradient-to-l from-transparent to-[#C9A97B]/20" />
            
            <p className="text-[#BFB5A5] text-lg mb-3">
              Diğer programlar sadece <span className="text-[#7A7067] line-through decoration-[#C9A97B]/30">"ne sattım?"</span> sorusuna cevap veriyor.
            </p>
            
            <div className="flex items-center justify-center gap-3 mb-3">
              <ArrowRight className="w-5 h-5 text-[#C9A97B]/50" />
            </div>
            
            <p className="text-xl lg:text-2xl font-display font-semibold text-gold-gradient max-w-3xl mx-auto">
              Tam Ayar ise "kime sattım, ne zaman tekrar satabilirim ve o anki kurdan kârım ne?" sorularını çözüyor.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
